/**
 * SOLID Review
 * S — Strong. One concern: a registered player with nothing stored — does the server still seat them somewhere?
 * O — Strong. Whether to look, and what a found room restores, are the Domain's decisions.
 * L — Strong. Implements its entry-step role exactly.
 * I — Strong. `take`.
 * D — Strong. The session's deps and the seat restore, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type { ICommandActiveRoomFound } from '../../domain/ports/schemas/SessionMoves';
import type { IConfigDepSession } from './IConfigDepSession';
import type { IServiceEntryStep } from './SessionEntryRoute';
import type { SeatRestore } from './SeatRestore';

const NOTHING: ICommandNothing = {};

/** The cold rejoin (FE2's useColdLoginRejoin): the server's live room for the player, restored — then the intent again. */
export class ColdRejoinStep implements IServiceEntryStep {
  constructor(private readonly deps: IConfigDepSession, private readonly seats: SeatRestore) {}

  async take(ctx: IContextRequest): Promise<boolean> {
    const { decisions, step, rooms } = this.deps;
    const plan = await step.run(ctx, (input) => decisions.planColdRejoin(ctx, input, NOTHING));
    if (!plan.success) return true;
    const found = await rooms.getActiveRoomForPlayer(ctx, plan.value);
    const answer: ICommandActiveRoomFound = { request: plan.value, room: found.success ? found.value : null };
    const decided = await step.run(ctx, (input) => decisions.activeRoomFound(ctx, input, answer));
    if (decided.success && decided.value.record !== null) await this.seats.restore(ctx, decided.value.record);
    return true;
  }
}
